/**
 * Subscribe & Save card, shared by the cart page and the product page.
 *
 * Built on Shopify's native selling plans rather than the subscription app's injected
 * widget: the app creates the plans, the theme reads variant.selling_plan_allocations
 * and submits the native `selling_plan` param.
 *
 * data-mode="cart"     converts every existing cart line to the chosen plan
 * data-mode="product"  enables the hidden selling_plan input and adds a subscription line
 */
class SubscriptionCard extends HTMLElement {
  connectedCallback() {
    this.mode = this.dataset.mode || 'cart';
    this.moneyFormat = this.dataset.moneyFormat || '';
    this.scopeTemplate = this.dataset.scopeTemplate || '';
    this.submitTemplate = this.dataset.submitTemplate || '';
    this.fromTemplate = this.dataset.fromTemplate || '';
    this.formId = this.dataset.formId || '';

    this.header = this.querySelector('.subscription-card-header');
    this.options = this.querySelector('.subscription-card-options');
    this.radios = Array.from(this.querySelectorAll('.subscription-card-plan-input'));
    this.scopeEl = this.querySelector('.subscription-card-scope');
    this.fromEl = this.querySelector('[data-from-price]');
    this.submitBtn = this.querySelector('.subscription-card-submit');
    this.removeBtn = this.querySelector('.subscription-card-remove');
    this.errorEl = this.querySelector('.subscription-card-error');

    if (this.header) this.header.addEventListener('click', this.toggle.bind(this));
    this.radios.forEach((radio) => radio.addEventListener('change', this.handlePlanChange.bind(this)));
    if (this.submitBtn) this.submitBtn.addEventListener('click', this.handleSubmit.bind(this));
    if (this.removeBtn) this.removeBtn.addEventListener('click', this.handleRemove.bind(this));

    if (this.mode === 'product') {
      // The quantity field lives in a block that variant-select.js replaces wholesale on
      // every variant change, so a direct listener would die with it. Delegate instead.
      this._onQuantityInput = (event) => {
        const el = event.target;
        if (el && el.name === 'quantity' && el.getAttribute('form') === this.formId) this.render();
      };
      document.addEventListener('input', this._onQuantityInput);
      document.addEventListener('change', this._onQuantityInput);
    }

    // Prices are already correct from Liquid. Only repaint once Alpine is up, otherwise
    // formatMoney is unavailable and we'd replace "$8.99" with a bare "8.99".
    if (this.moneyHelper) {
      this.render();
    } else {
      document.addEventListener('alpine:initialized', () => this.render(), { once: true });
    }
  }

  disconnectedCallback() {
    if (this._onQuantityInput) {
      document.removeEventListener('input', this._onQuantityInput);
      document.removeEventListener('change', this._onQuantityInput);
    }
  }

  get selected() {
    return this.radios.find((radio) => radio.checked) || this.radios[0];
  }

  get quantity() {
    if (this.mode !== 'product') return 1;
    const input = document.querySelector(`[name="quantity"][form="${this.formId}"]`);
    const qty = input ? parseInt(input.value, 10) : 1;
    return Number.isNaN(qty) || qty < 1 ? 1 : qty;
  }

  get form() {
    return this.formId ? document.getElementById(this.formId) : null;
  }

  get planInput() {
    // The input sits outside the <form> and is attached via the form="" attribute, so it
    // is reachable through form.elements but NOT through form.querySelector().
    const form = this.form;
    const named = form && form.elements ? form.elements.namedItem('selling_plan') : null;
    if (named) return named;
    return document.querySelector(`input[name="selling_plan"][form="${this.formId}"]`);
  }

  get moneyHelper() {
    const helper = window.Alpine && window.Alpine.store && window.Alpine.store('xHelper');
    return helper && typeof helper.formatMoney === 'function' ? helper : null;
  }

  formatMoney(cents) {
    const helper = this.moneyHelper;
    return helper ? helper.formatMoney(cents, this.moneyFormat) : (cents / 100).toFixed(2);
  }

  toggle() {
    if (!this.options) return;
    const isOpen = !this.options.classList.contains('hidden');
    this.options.classList.toggle('hidden', isOpen);
    if (this.header) this.header.setAttribute('aria-expanded', String(!isOpen));
  }

  handlePlanChange() {
    this.querySelectorAll('.subscription-card-plan').forEach((plan) => {
      plan.classList.toggle('is-selected', !!plan.querySelector('.subscription-card-plan-input:checked'));
    });
    this.render();
  }

  /** Repaints every price, the scope line and the button label for the current selection. */
  render() {
    const qty = this.quantity;
    let lowest = null;

    this.radios.forEach((radio) => {
      const total = (parseInt(radio.dataset.unitPrice, 10) || 0) * qty;
      if (lowest === null || total < lowest) lowest = total;
      const priceEl = radio.closest('.subscription-card-plan').querySelector('[data-plan-price]');
      if (priceEl) priceEl.textContent = `${this.formatMoney(total)} / ${radio.dataset.planCadence || ''}`;
    });

    if (this.fromEl && lowest !== null) {
      this.fromEl.textContent = this.fromTemplate.replace('__PRICE__', this.formatMoney(lowest));
    }

    const plan = this.selected;
    if (!plan) return;

    const total = (parseInt(plan.dataset.unitPrice, 10) || 0) * qty;
    const cadence = plan.dataset.planCadence || '';
    const cadenceLong = plan.dataset.planCadenceLong || cadence;

    if (this.scopeEl) this.scopeEl.textContent = this.scopeTemplate.replace('__FREQUENCY__', cadenceLong);

    const content = this.submitBtn && this.submitBtn.querySelector('.subscription-card-submit-content');
    if (content) {
      content.textContent = this.submitTemplate
        .replace('__PRICE__', this.formatMoney(total))
        .replace('__FREQUENCY__', cadence);
    }
  }

  setLoading(loading) {
    if (!this.submitBtn) return;
    const content = this.submitBtn.querySelector('.subscription-card-submit-content');
    const loader = this.submitBtn.querySelector('.subscription-card-submit-loader');
    this.submitBtn.disabled = loading;
    if (this.removeBtn) this.removeBtn.disabled = loading;
    if (content) content.classList.toggle('hidden', loading);
    if (loader) loader.classList.toggle('hidden', !loading);
  }

  showError(message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message;
    this.errorEl.classList.remove('hidden');
  }

  clearError() {
    if (this.errorEl) this.errorEl.classList.add('hidden');
  }

  handleSubmit(event) {
    event.preventDefault();
    const plan = this.selected;
    if (!plan || !plan.value) return;

    if (this.mode === 'product') {
      this.addSubscriptionLine(plan);
    } else {
      this.applyToEveryLine(plan.value);
    }
  }

  handleRemove(event) {
    event.preventDefault();
    this.applyToEveryLine(null);
  }

  /* ------------------------------------------------------------------ product mode */

  /**
   * Enables the hidden selling_plan input for one submit, then hands off to the theme's
   * own add-to-cart button so the loading state, notification and section re-render all
   * behave exactly as they do for a one-time add.
   */
  addSubscriptionLine(plan) {
    const input = this.planInput;
    const form = this.form;
    if (!input || !form) {
      this.showError(window.Eurus && window.Eurus.cart_error ? window.Eurus.cart_error : 'Something went wrong. Please try again.');
      console.error('[subscription-card] missing', !form ? `form #${this.formId}` : 'selling_plan input');
      return;
    }

    this.clearError();
    input.value = plan.dataset.planId || '';
    input.disabled = false;

    const reset = () => {
      input.value = '';
      input.disabled = true;
      document.removeEventListener('eurus:cart:items-changed', reset);
    };
    document.addEventListener('eurus:cart:items-changed', reset);
    // Fallback in case the add fails and the event never fires, so the next plain
    // Add to cart can't silently inherit the plan.
    setTimeout(reset, 8000);

    const addButton = form.querySelector('button[name="add"]')
      || document.querySelector(`button[name="add"][form="${this.formId}"]`);
    if (addButton) {
      addButton.click();
    } else {
      reset();
      this.showError(window.Eurus && window.Eurus.cart_error ? window.Eurus.cart_error : 'Something went wrong. Please try again.');
    }
  }

  /* --------------------------------------------------------------------- cart mode */

  /**
   * Converts every line in the cart to the given selling plan, or back to a one-time
   * purchase when planName is null. Lines are changed one at a time because each
   * cart/change.js call renumbers the remaining lines.
   */
  async applyToEveryLine(planName) {
    this.clearError();
    this.setLoading(true);

    try {
      const cart = await this.fetchCart();
      if (!cart || !cart.items.length) {
        this.setLoading(false);
        return;
      }

      const variantIds = cart.items.map((item) => item && (item.id || item.variant_id)).filter(Boolean);
      for (let i = 0; i < variantIds.length; i++) {
        await this.changeLine(variantIds[i], planName);
      }

      await this.refresh();
    } catch (error) {
      this.setLoading(false);
      this.showError(window.Eurus && window.Eurus.cart_error ? window.Eurus.cart_error : 'Something went wrong. Please try again.');
    }
  }

  async fetchCart() {
    const response = await fetch(window.Shopify.routes.root + 'cart.js', {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    });
    if (!response.ok) throw new Error('Failed to fetch cart');
    return response.json();
  }

  async changeLine(variantId, planName) {
    const cart = await this.fetchCart();
    const index = cart.items.findIndex((item) => (item && (item.id || item.variant_id)) === variantId);
    if (index === -1) return;

    const item = cart.items[index];
    const payload = {
      // change.js needs the 1-based line, not the line key, when altering selling_plan
      line: index + 1,
      quantity: item.quantity,
      selling_plan: planName ? this.getPlanId(item, planName) : null
    };
    if (planName && !payload.selling_plan) return;

    const response = await fetch(window.Shopify.routes.root + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    if (!response.ok) throw new Error('HTTP error! status: ' + response.status);
    return response.json();
  }

  getPlanId(item, planName) {
    const variantId = item && (item.id || item.variant_id);
    if (!variantId) return null;

    const plans = (window.CartSubscriptionVariantPlans && window.CartSubscriptionVariantPlans[variantId]) || null;
    if (Array.isArray(plans)) {
      const matched = plans.find((plan) => plan && plan.name === planName);
      if (matched && matched.id) {
        const parsed = parseInt(matched.id, 10);
        if (!Number.isNaN(parsed)) return parsed;
      }
    }

    const selected = this.selected;
    if (selected && selected.dataset.planId) {
      const parsed = parseInt(selected.dataset.planId, 10);
      if (!Number.isNaN(parsed)) return parsed;
    }
    return null;
  }

  /** Re-renders the cart sections so summary, totals and free shipping bar all follow. */
  async refresh() {
    const helper = window.Alpine && window.Alpine.store && window.Alpine.store('xCartHelper');
    if (!helper || typeof helper.reRenderSections !== 'function') {
      window.location.reload();
      return;
    }
    await helper.reRenderSections();
    document.dispatchEvent(new CustomEvent('eurus:cart:items-changed'));
  }
}

if (!customElements.get('subscription-card')) {
  customElements.define('subscription-card', SubscriptionCard);
}
